import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faCalendarDay, faCircleCheck, faGraduationCap, faSpinner, faTriangleExclamation } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import type { SubscriptionPriceProduct, SubscriptionType, Subscription, EuroCent } from '@/models';
import { PROMOTION_CONFIG, isPromotionActive, matchesPromotionAudience } from '@/consts/promotion';
import PricingCard, { type BillingPeriod } from '@/components/checkout/PricingCard';
import BoostCard from '@/components/checkout/BoostCard';
import PromotionBanner from '@/components/PromotionBanner';
import { PRICING_PACKAGES } from '@/consts/pricing';
import { useUser } from '@/contexts/UserContext';
import { useSubscriptionProducts } from '@/contexts/SubscriptionProductsContext';
import { canSeePricingPage, getCommunityUpsellState, getSubscriptionStatus, isProductTypeOwned, shouldOfferCommunityUpgrade } from '@/utils/subscription-status';
import { isEmptyFeatureConfig } from '@/models/feature-config';

export default function PricingPage() {
  const { user, isStaff, loading: userLoading } = useUser();
  const { products, loading: productsLoading, error: productsError } = useSubscriptionProducts();
  const hasNoFeatures = user !== null && !isStaff && isEmptyFeatureConfig(user.featureConfig);
  // Community-Mitgliedern bietet der Campus nur noch Boost und Daytrading an — alles
  // andere deckt ihre Mitgliedschaft bereits ab. Staff sieht die Seite immer vollständig.
  const upsell = getCommunityUpsellState(user);
  const isCommunityView = !userLoading && !isStaff && upsell.isCommunityMember;
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('6m');
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(true);
  const [subscriptionWarning, setSubscriptionWarning] = useState<string | null>(null);

  const loading = loadingSubscriptions || productsLoading;
  // Kein einziges aktives Produkt heißt: das Backend kommt gerade nicht an Stripe. Ohne
  // diesen Fall bliebe die Seite kommentarlos leer.
  const noProductsAvailable = !productsLoading && productsError === null && !products.some(p => p.active);
  const error = (productsError !== null || noProductsAvailable)
    ? 'Fehler beim Laden der Preise. Bitte versuchen Sie es später erneut.'
    : null;

  useEffect(() => {
    // Erst laden, wenn der User-Context aufgelöst ist — sonst würden kurz alle
    // Karten erscheinen (user === null) und gekaufte nach dem Nachladen verschwinden.
    if (userLoading) return;

    const fetchData = async () => {
      setLoadingSubscriptions(true);
      setSubscriptionWarning(null);

      // Fetch user subscriptions (non-blocking for the pricing cards)
      if (user !== null) {
        try {
          const subscriptionsResponse = await APIRoutes.getUserSubscriptions(user.id);
          setSubscriptions(subscriptionsResponse.subscriptions);
        } catch (err) {
          console.error('Failed to fetch user subscriptions:', err);
          setSubscriptionWarning('Dein Abonnement-Status konnte nicht geladen werden.');
        }
      }

      setLoadingSubscriptions(false);
    };

    fetchData();
  }, [user, userLoading]);

  // Get product by type
  const getProductByType = (type: SubscriptionType): SubscriptionPriceProduct | undefined => {
    return products.find(p => p.type === type && p.active);
  };

  // Get the 1-month price for a product type (used as base price for multi-month periods)
  const getMonthlyBasePrice = (productType: string): EuroCent | undefined => {
    const monthlyProduct = products.find(p => p.productType === productType && p.intervalMonths === 1);
    return monthlyProduct?.price;
  };

  // Pakete des gewählten Zeitraums mit aktivem Backend-Produkt, inkl. Besitz-Status.
  // "owned" kombiniert den Status aus den geladenen Abos mit dem user-basierten Check
  // (featureConfig + aktive Produktfamilien) — so bleibt die Seite konsistent zur
  // Sidebar-Bedingung, auch wenn der Abo-Fetch fehlschlägt.
  const periodPackages = PRICING_PACKAGES
    .filter(config => config.period === billingPeriod)
    .flatMap(config => {
      const product = getProductByType(config.type);
      if (product === undefined) return [];
      const status = getSubscriptionStatus(product, subscriptions, getProductByType, user?.featureConfig ?? null);
      const owned = status !== 'NOT_SUBSCRIBED'
        || (product.productType !== null && isProductTypeOwned(
          product.productType,
          user?.featureConfig ?? null,
          user?.activeSubscriptionProductTypes ?? []
        ));
      return [{ config, product, status, owned }];
    });

  // Beworben werden nur Pakete, die der Nutzer noch nicht gekauft hat bzw. die nicht
  // bereits durch ein anderes Paket abgedeckt sind. Community-Mitglieder sehen davon
  // ausschließlich Daytrading — Trade und Cashflow sind für sie kein Thema mehr.
  const visiblePackages = periodPackages
    .filter(entry => !entry.owned)
    .filter(entry => !isCommunityView || entry.product.productType === 'DAYTRADING');
  const hiddenOwnedCount = periodPackages.length - visiblePackages.length;
  const packagesReady = !userLoading && !loading && error === null;
  // Boost wird nicht verkauft, sondern nur beworben — daher als eigene Karte neben den Paketen.
  const showBoostCard = isCommunityView && upsell.showBoost;
  const cardCount = visiblePackages.length + (showBoostCard ? 1 : 0);
  // Nur wenn tatsächlich Pakete wegen Besitz ausgeblendet wurden — nie bei Ladefehlern.
  // Für Community-Mitglieder irrelevant: wer dort nichts mehr sieht, wird weitergeleitet.
  const showAllOwnedState = packagesReady && !isCommunityView && visiblePackages.length === 0 && hiddenOwnedCount > 0;
  // Cashflow-/Daytrading-Kunden (bzw. wer alle Pakete hat) bekommen als nächsten Schritt
  // die Ausbildung / 100K Community angeboten — im Campus gibt es für sie nichts Höheres.
  const showCommunityUpgrade = packagesReady && shouldOfferCommunityUpgrade(user, products);

  // Community-Mitglied mit Boost UND Daytrading: hier gibt es nichts mehr zu holen.
  // Zweite Bedingung fängt den Fall ab, dass die Karten aus einem anderen Grund wegfallen
  // (z. B. aktives Daytrading-Abo, dessen Feature noch nicht in der featureConfig steht) —
  // sonst bliebe eine leere Seite stehen. Staff bleibt ausgenommen, damit die Seite
  // weiterhin inspiziert werden kann.
  const communityViewEmpty = isCommunityView && packagesReady && cardCount === 0;
  if (!userLoading && !isStaff && (!canSeePricingPage(user) || communityViewEmpty)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen pb-12">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <Link
            to="/dashboard"
            className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Campus Upgrade
          </h1>
        </div>
      </div>

      {/* No-Features Banner */}
      {hasNoFeatures && !isCommunityView && (
        <div className="max-w-4xl mx-auto mb-8">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-5 border border-yellow-200 dark:border-yellow-800">
            <div className="flex items-start">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3 flex-shrink-0"
              />
              <div>
                <p className="text-yellow-900 dark:text-yellow-100 font-semibold">
                  Kein aktiver Plan – kein Zugriff auf geschützte Inhalte
                </p>
                <p className="text-yellow-800 dark:text-yellow-200 text-sm mt-1">
                  Du hast aktuell keinen aktiven Plan und damit keinen Zugriff auf die geschützten Bereiche des Campus. Wähle unten einen Plan, um wieder Zugriff zu erhalten.
                </p>
                <p className="text-yellow-700 dark:text-yellow-300 text-xs mt-2">
                  Hinweis: Accounts ohne aktiven Plan werden bei längerer Inaktivität automatisch gelöscht.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="text-center mb-12">
        {isCommunityView ? (
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Diese Bereiche kannst du zusätzlich zu deiner Community-Mitgliedschaft freischalten.
          </p>
        ) : (
        <>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
          Mehr Klarheit. Mehr Tiefe. Mehr Struktur.
        </h2>
        <p className="font-serif text-gray-900 dark:text-white max-w-3xl mx-auto  mb-2">
          Im Börsencampus erhältst du regelmäßige Einblicke in aktuelle Marktanalysen, Tradingideen und mögliche Szenarien aus der Praxis eines aktiven Traders.
        </p>
        <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto mb-2">
          Dabei geht es nicht darum, fertige Handelssignale zu kopieren oder blind umzusetzen. Ziel ist es vielmehr zu verstehen, wie erfahrene Marktteilnehmer Chancen identifizieren, Risiken einschätzen und Handelsideen entwickeln.
        </p>
        {/*<ul className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-4 list-disc list-inside space-y-1 inline-block text-left">
          <li>aktuelle Marktanalysen</li>
          <li>mögliche Tradingideen aus verschiedenen Märkten</li>
          <li>Einblicke in Entscheidungsprozesse eines Traders</li>
          <li>Erläuterungen zu Chancen- und Risikostrukturen</li>
        </ul>*/}
        <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-2">
          Wenn du deinen Campus erweiterst, öffnest du dir zusätzliche Bereiche, die dein Investieren und Trading noch klarer und fundierter machen.
        </p>


        {/* Consultation CTA */}
        {/*{!loading && error === null && (*/}
        {/*  <div className="max-w-2xl mx-auto mt-12 text-center">*/}
            <p className="text-gray-600 dark:text-gray-400 mb-4 font-bold">
              Du hast noch Fragen oder es gibt noch Unklarheiten? <br />
              Buche jetzt einfach ein Strategiegespräch.
            </p>
            <a
              href="https://calendly.com/academy-svenbolz/trading-academy-kennenlerngesprach"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors cursor-pointer"
            >
              <FontAwesomeIcon icon={faCalendarDay} className="w-4 h-4 mr-2" />
              Strategiegespräch buchen
            </a>
        {/*  </div>*/}
        {/*)}*/}
        </>
        )}

        {/* Billing Toggle — überflüssig, wenn nichts mehr zu buchen ist bzw. wenn einem
            Community-Mitglied nur die Boost-Karte (ohne Laufzeit) angezeigt wird */}
        {!showAllOwnedState && (!isCommunityView || visiblePackages.length > 0) && (
        <div className="flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-full p-1 border border-gray-200 dark:border-gray-700 mt-6 mx-auto w-fit">
          <button
            onClick={() => setBillingPeriod('1m')}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
              billingPeriod === '1m'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            1 Monat
          </button>
          <button
            onClick={() => setBillingPeriod('6m')}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
              billingPeriod === '6m'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            6 Monate
            <span className="ml-2 text-xs">-10%</span>
          </button>
          {/* 12-Monats-Option temporär deaktiviert
          <button
            onClick={() => setBillingPeriod('12m')}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
              billingPeriod === '12m'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            12 Monate
            <span className="ml-2 text-xs">-10%</span>
          </button>
          */}
        </div>
        )}

        {/* Promotion Banner */}
        {!showAllOwnedState
          && !isCommunityView
          && isPromotionActive(PROMOTION_CONFIG)
          && matchesPromotionAudience(PROMOTION_CONFIG.audience, user?.featureConfig ?? null)
          && <PromotionBanner config={PROMOTION_CONFIG} />}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-20">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      )}

      {/* Error State */}
      {error !== null && (
        <div className="max-w-xl mx-auto">
          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-6 border border-red-200 dark:border-red-800 text-center">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        </div>
      )}

      {/* Subscription Warning (non-blocking) */}
      {subscriptionWarning !== null && (
        <div className="max-w-xl mx-auto mb-6">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800 text-center">
            <p className="text-yellow-800 dark:text-yellow-200 text-sm">{subscriptionWarning}</p>
          </div>
        </div>
      )}

      {/* Pricing Grid — nur noch nicht gekaufte Pakete; Breite passt sich der Kartenzahl an.
          Die Boost-Karte steht im selben Grid, damit das Subgrid der Karten greift. */}
      {packagesReady && cardCount > 0 && (
        <div
          className={`grid grid-cols-1 gap-6 mx-auto items-stretch ${
            cardCount === 1
              ? 'max-w-sm'
              : cardCount === 2
                ? 'md:grid-cols-2 max-w-4xl'
                : 'md:grid-cols-2 xl:grid-cols-3 max-w-6xl'
          }`}
        >
          {visiblePackages.map(({ config, product, status }) => (
            <PricingCard
              key={config.type}
              product={product}
              billingPeriod={billingPeriod}
              name={config.name}
              subtitle={config.subtitle}
              shortDescription={config.shortDescription}
              features={config.features}
              idealFor={config.idealFor}
              popular={config.popular}
              subscriptionStatus={status}
              monthlyBasePrice={billingPeriod !== '1m' ? getMonthlyBasePrice(product.productType ?? '') : undefined}
            />
          ))}
          {showBoostCard && <BoostCard />}
        </div>
      )}

      {/* Alles freigeschaltet: alle beworbenen Pakete gekauft bzw. durch ein Paket abgedeckt */}
      {showAllOwnedState && (
        <div className="max-w-2xl mx-auto">
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-8 border border-green-200 dark:border-green-800 text-center">
            <FontAwesomeIcon icon={faCircleCheck} className="w-10 h-10 text-green-600 dark:text-green-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Du hast bereits vollen Zugriff
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-1">
              {showCommunityUpgrade
                ? 'Alle Pakete des Börsencampus sind für dich freigeschaltet.'
                : 'Alle Pakete des Börsencampus sind für dich freigeschaltet – aktuell gibt es nichts zu ergänzen.'}
            </p>
            <p className={`text-sm text-gray-600 dark:text-gray-400 ${showCommunityUpgrade ? '' : 'mb-6'}`}>
              Deine Abos und Laufzeiten kannst du jederzeit in den{' '}
              <Link to="/user-settings" className="text-blue-600 dark:text-blue-400 hover:underline">
                Einstellungen
              </Link>{' '}
              einsehen und verwalten.
            </p>
            {!showCommunityUpgrade && (
              <Link
                to="/dashboard"
                className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors cursor-pointer"
              >
                Zurück zum Dashboard
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Ausbildung / 100K Community — für Cashflow-, Daytrading- und Vollzugriff-Kunden */}
      {showCommunityUpgrade && (
        <div className={`max-w-3xl mx-auto ${showAllOwnedState ? 'mt-8' : 'mt-14'}`}>
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-8 border border-blue-200 dark:border-blue-800 text-center">
            <FontAwesomeIcon icon={faGraduationCap} className="w-10 h-10 text-blue-600 dark:text-blue-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              Wenn du tiefer gehen willst: Ausbildung &amp; 100K Community
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-2">
              Du nutzt bereits die Tradingideen des Campus. In der Ausbildung und der 100K Community lernst du,
              wie du solche Setups selbst findest, bewertest und eigenständig umsetzt.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Persönliche Begleitung, komplette Strategie-Ausbildung und der Austausch mit Gleichgesinnten –
              begrenzte Plätze, daher nach einem Kennenlerngespräch.
            </p>
            <a
              href="https://www.svenbolz.com/tradingausbildung-100k-community"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors cursor-pointer"
            >
              Mehr zur 100K Community
              <FontAwesomeIcon icon={faArrowRight} className="w-4 h-4" />
            </a>
          </div>
        </div>
      )}

      {/* Info Section */}
      {/*<div className="max-w-7xl mx-auto mt-12 text-center">
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Du willst verstehen, wie diese Einstiege entstehen?</h3>
          <p className="text-gray-600 dark:text-gray-400">
            Wenn du lernen möchtest, wie du selbst systematisch zu solchen Setups kommst, findest du alles dazu in der Ausbildung.
          </p>
          <a
            href="https://www.svenbolz.com/tradingausbildung-100k-community"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-4 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors cursor-pointer"
          >
            👉 Hier zur Ausbildung
          </a>
        </div>
      </div>*/}
    </div>
  );
}
